import { useEffect, useState, useCallback, ReactNode } from 'react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { DropdownItem } from '../../components/Dropdown/DropdownModels';
import FilterTag from '../../components/FilterTag/FilterTag';
import { ClearAllTagsContainer } from '../../components/FilterTag/FilterTagStyle';

import useSnackBar from '../../hooks/useSnackBar/useSnackBar';

import { TransactionsCounters, TransactionsFilter } from '../../models/TransactionsModels';

import TransactionsService from '../../services/Transactions/TransactionsService';

import { removeFilter, resetFilter } from '../../stores/TransactionsStore';
import { RootState } from '../../stores/Store';

function useTransactionsTableFilters() {
  const { language } = i18next;
  const dispatch = useDispatch();
  const { filter } = useSelector((state: RootState) => state.transactionsReducer);
  const [transactionTypesOptions, setTransactionTypesOptions] = useState<DropdownItem[]>([]);
  const [transactionCounters, setTransactionCounters] = useState<TransactionsCounters>({
    submitted: 0,
    processing: 0,
    completed: 0,
    error: 0,
  });
  const [countersLoading, setCountersLoading] = useState(false);
  const snackBar = useSnackBar();
  const [t] = useTranslation();

  useEffect(() => {
    TransactionsService.api
      .fetchTransactionTypes()
      .then((response) => {
        const transactionTypesDropdownOptions = [
          ...response.data.transactionTypes.map((obj) => ({
            key: obj,
            value: obj,
            label: t(`pages.transactions.transactionTypes.${obj}`),
          })),
        ];
        setTransactionTypesOptions(transactionTypesDropdownOptions);
      })
      .catch(() => {
        snackBar.showSnackBar(t('pages.transactions.fetchTransactionTypesError'), 'error');
      });
  }, [language]);

  /**
   * Fetch transaction counters request
   */
  const fetchTransactionCounters = () => {
    if (!filter.type) return;

    setCountersLoading(true);
    TransactionsService.api
      .fetchTransactionCounters({ transactionType: filter.type })
      .then((response) => {
        setTransactionCounters(response.data);
      })
      .catch(() => {
        snackBar.showSnackBar(t('pages.transactions.fetchTransactionCountersError'), 'error');
      })
      .finally(() => {
        setCountersLoading(false);
      });
  };

  useEffect(() => {
    fetchTransactionCounters();
  }, [filter.type]);

  /**
   * Removes the filter from the store
   * @param filterKey
   */
  const onRemoveFilter = (filterKey: keyof TransactionsFilter) => {
    dispatch(removeFilter(filterKey));
  };

  /**
   * Applies the proper transformations to a given type of filter tag
   * @param filterKey
   */
  const filterTagAdapter = (filterKey: keyof TransactionsFilter) => {
    if (filter[filterKey] === '') return null;
    switch (filterKey) {
      case 'filter':
      case 'type':
        return null;
      case 'status':
        return (
          <FilterTag key={filterKey} onRemove={() => onRemoveFilter(filterKey)}>
            {t('pages.transactions.table.filters.status') + t(`pages.transactions.table.status.${filter[filterKey]}`)}
          </FilterTag>
        );
      default:
        return null;
    }
  };

  const renderFilterTags = useCallback(() => {
    const existingFilterKeys = Object.keys(filter);
    const filterTags: ReactNode[] = [];
    existingFilterKeys.forEach((fk) => {
      const f = filterTagAdapter(fk as keyof TransactionsFilter);
      if (f) {
        filterTags.push(f);
      }
    });

    if (filterTags.length > 0) {
      filterTags.push(
        <ClearAllTagsContainer key="clearAll" onClick={() => dispatch(resetFilter())}>
          {t('common.clearAll')}
        </ClearAllTagsContainer>,
      );
    }

    return filterTags;
  }, [filter]);

  return { transactionTypesOptions, transactionCounters, countersLoading, fetchTransactionCounters, renderFilterTags };
}

export default useTransactionsTableFilters;
